
import React, { useState, useEffect } from 'react';
import { Edit, ChevronDown, ChevronRight, Expand, Minimize2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
    fetchChecklistTemplate,
    fetchChecklistProgress,
    updateChecklistProgress
} from '../../services/apiService';

const ChecklistTask = ({ task, status, onStatusChange, saving }) => {
    const [notes, setNotes] = useState(status?.notes || '');
    const [isNotesVisible, setIsNotesVisible] = useState(false);

    const handleCheckboxChange = (e) => {
        onStatusChange(task.id, { completed: e.target.checked, notes });
    };

    const handleNotesSave = () => {
        onStatusChange(task.id, { completed: status?.completed || false, notes });
        setIsNotesVisible(false);
    };

    return (
        <div className="px-6 py-3 border-b border-gray-200 last:border-b-0 hover:bg-white transition-colors duration-150">
            <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                    <input
                        type="checkbox"
                        checked={status?.completed || false}
                        onChange={handleCheckboxChange}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        disabled={saving}
                    />
                    <div className="flex-1">
                        <label className={`text-sm leading-5 cursor-pointer ${status?.completed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                            {task.task}
                        </label>
                        {status?.notes && !isNotesVisible && (
                            <p className="mt-2 text-xs text-gray-600 bg-gray-100 p-2 rounded border-l-4 border-blue-200">
                                <span className="font-medium">Note:</span> {status.notes}
                            </p>
                        )}
                    </div>
                </div>
                <button
                    onClick={() => setIsNotesVisible(!isNotesVisible)}
                    className="ml-2 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors duration-150"
                    disabled={saving}
                    title="Add/edit notes"
                >
                    <Edit size={14} />
                </button>
            </div>
            {isNotesVisible && (
                <div className="mt-3 ml-7 space-y-3">
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="w-full p-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={3}
                        placeholder="Add notes about this task..."
                        disabled={saving}
                    />
                    <div className="flex justify-end gap-2">
                        <button
                            onClick={() => setIsNotesVisible(false)}
                            disabled={saving}
                            className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-150"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleNotesSave}
                            className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors duration-150"
                            disabled={saving}
                        >
                            Save Note
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const DealershipChecklistPage = ({ dealership, onUpdateDealership }) => {
    const [masterTemplate, setMasterTemplate] = useState({ sales: [], service: [] });
    const [checklistProgress, setChecklistProgress] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [expandedCategories, setExpandedCategories] = useState({});

    useEffect(() => {
        loadData();
    }, [dealership.id]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [template, progress] = await Promise.all([
                fetchChecklistTemplate(),
                fetchChecklistProgress(dealership.id)
            ]);
            setMasterTemplate(template);
            setChecklistProgress(progress);

            // Initialize all categories as expanded by default
            const allCategories = {};
            ['sales', 'service'].forEach(type => {
                const validTasks = (template[type] || []).filter(task => task && task.id && task.category);
                const categories = [...new Set(validTasks.map(t => t.category))];
                categories.forEach(category => {
                    allCategories[`${type}-${category}`] = true;
                });
            });
            setExpandedCategories(allCategories);
        } catch (error) {
            console.error('Error loading checklist data:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleCategory = (type, category) => {
        const key = `${type}-${category}`;
        setExpandedCategories(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const expandAllCategories = (type) => {
        const validTasks = (masterTemplate[type] || []).filter(task => task && task.id && task.category);
        const categories = [...new Set(validTasks.map(t => t.category))];

        setExpandedCategories(prev => {
            const updates = {};
            categories.forEach(category => {
                updates[`${type}-${category}`] = true;
            });
            return { ...prev, ...updates };
        });
    };

    const collapseAllCategories = (type) => {
        const validTasks = (masterTemplate[type] || []).filter(task => task && task.id && task.category);
        const categories = [...new Set(validTasks.map(t => t.category))];

        setExpandedCategories(prev => {
            const updates = {};
            categories.forEach(category => {
                updates[`${type}-${category}`] = false;
            });
            return { ...prev, ...updates };
        });
    };

    const areAllCategoriesExpanded = (type) => {
        const validTasks = (masterTemplate[type] || []).filter(task => task && task.id && task.category);
        const categories = [...new Set(validTasks.map(t => t.category))];
        return categories.every(category => expandedCategories[`${type}-${category}`]);
    };

    const calculateCategoryProgress = (type, category) => {
        const validTasks = (masterTemplate[type] || []).filter(task =>
            task && task.id && task.category === category
        );
        const total = validTasks.length;
        if (total === 0) return { completed: 0, total: 0, percentage: 0 };
        const completed = validTasks.reduce((count, task) =>
            checklistProgress[task.id]?.completed ? count + 1 : count, 0
        );
        return { completed, total, percentage: Math.round((completed / total) * 100) };
    };

    const handleStatusChange = async (taskId, newStatus) => {
        try {
            setSaving(true);
            await updateChecklistProgress(
                dealership.id,
                taskId,
                newStatus.completed,
                newStatus.notes
            );
            // Update local state
            setChecklistProgress(prev => ({
                ...prev,
                [taskId]: newStatus
            }));
        } catch (error) {
            console.error('Error updating checklist progress:', error);
            alert('Failed to update checklist progress. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const calculateProgress = (type) => {
        const validTasks = (masterTemplate[type] || []).filter(task => task && task.id && task.category);
        const total = validTasks.length;
        if (total === 0) return 0;
        const completed = validTasks.reduce((count, task) => checklistProgress[task.id]?.completed ? count + 1 : count, 0);
        return Math.round((completed / total) * 100);
    };

    const renderChecklistSection = (type) => {
        const validTasks = (masterTemplate[type] || []).filter(task => task && task.id && task.category);
        const categories = [...new Set(validTasks.map(t => t.category))].sort();

        return (
            <div className="bg-white rounded-xl shadow-md border">
                {/* Section Header */}
                <div className="p-6 border-b">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold capitalize">{type} Checklist</h3>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => expandAllCategories(type)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-800 transition-colors duration-150"
                                title="Expand all categories"
                            >
                                <Expand size={14} />
                                Expand All
                            </button>
                            <button
                                onClick={() => collapseAllCategories(type)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-800 transition-colors duration-150"
                                title="Collapse all categories"
                            >
                                <Minimize2 size={14} />
                                Collapse All
                            </button>
                        </div>
                    </div>
                    <div className="flex justify-between mb-2">
                        <span className="text-sm text-gray-600">Overall Progress</span>
                        <span className="font-bold text-blue-600">{calculateProgress(type)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                             style={{ width: `${calculateProgress(type)}%` }}>
                        </div>
                    </div>
                </div>

                {/* Category Sections */}
                <div className="divide-y">
                    {categories.map(category => {
                        const categoryTasks = validTasks.filter(t => t.category === category);
                        const categoryKey = `${type}-${category}`;
                        const isExpanded = expandedCategories[categoryKey];
                        const progress = calculateCategoryProgress(type, category);

                        return (
                            <div key={category} className="border-gray-100">
                                {/* Category Header - Collapsible */}
                                <button
                                    onClick={() => toggleCategory(type, category)}
                                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors duration-150 focus:outline-none focus:bg-gray-50"
                                >
                                    <div className="flex items-center gap-3">
                                        {isExpanded ? (
                                            <ChevronDown size={20} className="text-gray-500" />
                                        ) : (
                                            <ChevronRight size={20} className="text-gray-500" />
                                        )}
                                        <div className="text-left">
                                            <h4 className="font-semibold text-gray-900">{category}</h4>
                                            <p className="text-sm text-gray-500">
                                                {progress.completed} of {progress.total} tasks completed
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="text-right">
                                            <span className="text-sm font-medium text-gray-900">
                                                {progress.percentage}%
                                            </span>
                                        </div>
                                        <div className="w-16 bg-gray-200 rounded-full h-2">
                                            <div
                                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                                style={{ width: `${progress.percentage}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </button>

                                {/* Category Tasks - Collapsible Content */}
                                {isExpanded && (
                                    <div className="bg-gray-50">
                                        {categoryTasks.map(task => (
                                            <ChecklistTask
                                                key={task.id}
                                                task={task}
                                                status={checklistProgress[task.id]}
                                                onStatusChange={handleStatusChange}
                                                saving={saving}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-gray-500">Loading checklist...</div>
            </div>
        );
    }

    return (
        <div>
            <div className="mb-6">
                <Link to={`/setup/${dealership.id}`} className="text-sm text-blue-600 hover:underline">
                    &larr; Back to {dealership.name}
                </Link>
            </div>
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-3xl font-bold">{dealership.name} - Onboarding Checklist</h2>
                {saving && (
                    <div className="text-sm text-blue-600">Saving changes...</div>
                )}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {renderChecklistSection('sales')}
                {renderChecklistSection('service')}
            </div>
        </div>
    );
};

export default DealershipChecklistPage;
